// AUTO-WA-39: how Meta prices WhatsApp messages, in plain words and numbers.
// Source: Meta's "Pricing on the WhatsApp Business Platform" page and its rate
// cards effective 1 October 2026 (INR and USD). Meta may change rates only on
// 1 January, 1 April, 1 July and 1 October; update `data/` and RATE_CARD_DATE then.

export const RATE_CARD_DATE = '2026-10-01';
export const PRICING_SOURCE = 'https://developers.facebook.com/documentation/business-messaging/whatsapp/pricing';
export const BILLING_HUB_URL = 'https://business.facebook.com/billing_hub/accounts';
export const SERVICE_FREE_TIER = 1000;

// Loaded on demand so the 90 KB rate card is not in the main bundle.
let rateCardPromise = null;
export function loadRateCard() {
  if (!rateCardPromise) {
    rateCardPromise = import('./data/rateCard-2026-10-01.json')
      .then((module) => module.default || module)
      .catch((error) => { rateCardPromise = null; throw error; });
  }
  return rateCardPromise;
}

// The kinds of messages Meta prices differently. `rate` is the rate-card column.
export const CATEGORIES = [
  {
    id: 'marketing', rate: 'marketing', label: 'Marketing', color: '#147DB5',
    short: 'Offers, festive wishes, new arrivals',
    when: 'Charged every time one is delivered.',
    example: 'Diwali sale: 20% off all sweet boxes till Sunday!',
  },
  {
    id: 'utility', rate: 'utility', label: 'Utility', color: '#1F9D57',
    short: 'Order updates, reminders, receipts',
    when: 'Charged every time one is delivered, inside or outside the 24-hour window (from 1 Oct 2026).',
    example: 'Your order #1042 is packed and will reach you today.',
  },
  {
    id: 'authentication', rate: 'authentication', label: 'Authentication', color: '#C98A12',
    short: 'One-time passwords (OTP)',
    when: 'Charged every time one is delivered. Some businesses pay a higher international rate.',
    example: '482913 is your login code. Do not share it.',
  },
  {
    id: 'service', rate: 'service', label: 'Service replies', color: '#2E8FA3',
    short: 'Your normal replies in a chat',
    when: 'First 1,000 a month per number are free; after that each delivered reply is charged (from 1 Oct 2026).',
    example: 'Yes, we deliver to Whitefield. Shall I book it?',
  },
];
export const categoryById = (id) => CATEGORIES.find((category) => category.id === id) || null;

// Meta's pricing_analytics categories → the words people see.
const PRICING_CATEGORY_LABELS = {
  MARKETING: 'Marketing', MARKETING_LITE: 'Marketing (Lite)', UTILITY: 'Utility', AUTHENTICATION: 'Authentication',
  AUTHENTICATION_INTERNATIONAL: 'Authentication (international)', SERVICE: 'Service replies',
  REFERRAL_CONVERSION: 'From ads (free)', GROUP_SERVICE: 'Group replies', GROUP_UTILITY: 'Group utility', GROUP_MARKETING: 'Group marketing',
};
const PRICING_CATEGORY_COLORS = {
  MARKETING: '#147DB5', MARKETING_LITE: '#4FA3D1', UTILITY: '#1F9D57', AUTHENTICATION: '#C98A12',
  AUTHENTICATION_INTERNATIONAL: '#A66A08', SERVICE: '#2E8FA3', REFERRAL_CONVERSION: '#7CC4E8',
};
export const pricingCategoryLabel = (key) => PRICING_CATEGORY_LABELS[String(key || '').toUpperCase()] || String(key || 'Other').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
export const pricingCategoryColor = (key) => PRICING_CATEGORY_COLORS[String(key || '').toUpperCase()] || '#8CA1B2';

// ISO country → rate-card market (Meta's mapping, with the 9 markets that became
// standalone on 1 Oct 2026). Anything not listed is "Other".
const STANDALONE = {
  AR: 'Argentina', BD: 'Bangladesh', BR: 'Brazil', CL: 'Chile', CO: 'Colombia', EG: 'Egypt', FR: 'France', DE: 'Germany',
  HK: 'Hong Kong', HU: 'Hungary', IN: 'India', ID: 'Indonesia', IQ: 'Iraq', IL: 'Israel', IT: 'Italy', KZ: 'Kazakhstan',
  KW: 'Kuwait', MY: 'Malaysia', MX: 'Mexico', MA: 'Morocco', NL: 'Netherlands', NP: 'Nepal', NG: 'Nigeria', OM: 'Oman',
  PK: 'Pakistan', PE: 'Peru', PL: 'Poland', QA: 'Qatar', RO: 'Romania', RU: 'Russia', SA: 'Saudi Arabia', SG: 'Singapore',
  ZA: 'South Africa', ES: 'Spain', LK: 'Sri Lanka', TR: 'Turkey', UA: 'Ukraine', AE: 'United Arab Emirates', GB: 'United Kingdom',
  US: 'North America', CA: 'North America',
};
const REGIONS = {
  'Rest of Africa': 'DZ AO BJ BW BF BI CM TD CG ER ET GA GM GH GW CI KE LS LR LY MG MW ML MR MZ NA NE RW SN SL SO SS SD SZ TZ TG TN UG ZM ZW',
  'Rest of Asia Pacific': 'AF AU KH CN JP LA MN NZ PG PH TW TJ TH TM UZ VN',
  'Rest of Central & Eastern Europe': 'AL AM AZ BY BG HR CZ GE GR LV LT MD MK RS SK SI',
  'Rest of Western Europe': 'AT BE DK FI IE NO PT SE CH',
  'Rest of Latin America': 'BO CR DO EC SV GT HT HN JM NI PA PY PR UY VE',
  'Rest of Middle East': 'BH JO LB YE',
};
const ISO_MARKET = { ...STANDALONE };
Object.entries(REGIONS).forEach(([market, codes]) => codes.split(' ').forEach((code) => { ISO_MARKET[code] = market; }));
export const marketForIso = (iso) => ISO_MARKET[String(iso || '').toUpperCase()] || 'Other';

// Longest calling-code prefix wins (+971 before +97, +1 last). Used only to
// guess a sensible default market from the business number.
const CALLING_CODES = [
  ['880', 'Bangladesh'], ['852', 'Hong Kong'], ['977', 'Nepal'], ['974', 'Qatar'], ['971', 'United Arab Emirates'], ['972', 'Israel'],
  ['966', 'Saudi Arabia'], ['965', 'Kuwait'], ['968', 'Oman'], ['964', 'Iraq'], ['380', 'Ukraine'], ['234', 'Nigeria'], ['212', 'Morocco'],
  ['91', 'India'], ['92', 'Pakistan'], ['94', 'Sri Lanka'], ['90', 'Turkey'], ['62', 'Indonesia'], ['60', 'Malaysia'], ['65', 'Singapore'],
  ['55', 'Brazil'], ['54', 'Argentina'], ['56', 'Chile'], ['57', 'Colombia'], ['51', 'Peru'], ['52', 'Mexico'], ['20', 'Egypt'],
  ['27', 'South Africa'], ['33', 'France'], ['49', 'Germany'], ['39', 'Italy'], ['34', 'Spain'], ['31', 'Netherlands'], ['44', 'United Kingdom'],
  ['48', 'Poland'], ['40', 'Romania'], ['36', 'Hungary'], ['7', 'Russia'], ['1', 'North America'],
];
export function marketForPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  const hit = CALLING_CODES.find(([code]) => digits.startsWith(code));
  return hit ? hit[1] : null;
}

// Meta bills in the WhatsApp Business account's currency; we carry INR and USD cards.
export const cardCurrency = (accountCurrency) => (String(accountCurrency || '').toUpperCase() === 'USD' ? 'USD' : String(accountCurrency || '').toUpperCase() === 'INR' ? 'INR' : null);

const SYMBOL = { INR: '₹', USD: '$' };
export function formatMoney(value, currency = 'INR', { precise = false } = {}) {
  const amount = Number(value) || 0;
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  const digits = precise ? (amount !== 0 && Math.abs(amount) < 1 ? 4 : 2) : (Math.abs(amount) >= 100 ? 0 : 2);
  const text = amount.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return SYMBOL[currency] ? `${SYMBOL[currency]}${text}` : `${text} ${currency}`;
}
export const formatRate = (value, currency) => (value === null || value === undefined ? '—' : formatMoney(value, currency, { precise: true }));

export const findMarket = (card, currency, market) => card?.currencies?.[currency]?.markets?.find((row) => row.market === market) || null;
export const marketNames = (card, currency = 'INR') => (card?.currencies?.[currency]?.markets || []).map((row) => row.market);

// Charge for `count` messages in one month across Meta's volume tiers.
export function tieredCost(tiers, count, listRate = 0) {
  let remaining = Math.max(0, Math.floor(Number(count) || 0));
  if (!remaining) return 0;
  if (!tiers?.length) return remaining * listRate;
  let total = 0;
  for (const tier of tiers) {
    const size = tier.to === null ? Infinity : tier.to - (tier.from === 0 ? 0 : tier.from - 1);
    const used = Math.min(remaining, size);
    total += used * tier.rate;
    remaining -= used;
    if (!remaining) break;
  }
  return total;
}

// A month's estimate for one market. `numbers` business numbers each get 1,000 free replies.
export function estimateMonth(market, { marketing = 0, utility = 0, authentication = 0, service = 0, numbers = 1 } = {}) {
  if (!market) return { lines: [], total: 0 };
  const freeReplies = SERVICE_FREE_TIER * Math.max(1, Number(numbers) || 1);
  const billableReplies = Math.max(0, (Number(service) || 0) - freeReplies);
  const lines = [
    { id: 'marketing', count: Number(marketing) || 0, billable: Number(marketing) || 0, cost: (Number(marketing) || 0) * (market.marketing || 0), rate: market.marketing },
    { id: 'utility', count: Number(utility) || 0, billable: Number(utility) || 0, cost: tieredCost(market.tiers?.utility, utility, market.utility), rate: market.utility },
    { id: 'authentication', count: Number(authentication) || 0, billable: Number(authentication) || 0, cost: tieredCost(market.tiers?.authentication, authentication, market.authentication), rate: market.authentication },
    { id: 'service', count: Number(service) || 0, billable: billableReplies, free: Math.min(Number(service) || 0, freeReplies), cost: billableReplies * (market.service || 0), rate: market.service },
  ];
  return { lines, total: lines.reduce((sum, line) => sum + line.cost, 0), freeReplies };
}

export const PRESETS = [
  { id: 'shop', label: 'Local shop', values: { marketing: 400, utility: 600, authentication: 0, service: 1500 } },
  { id: 'brand', label: 'Growing brand', values: { marketing: 5000, utility: 8000, authentication: 2000, service: 6000 } },
  { id: 'otp', label: 'App with OTPs', values: { marketing: 0, utility: 3000, authentication: 40000, service: 800 } },
];

// What changed and when — newest first. Dates are when Meta applied them.
export const PRICING_TIMELINE = [
  { date: '2026-10-01', title: 'Replies and in-window utility messages start costing money', text: 'Service replies are charged at the utility rate after 1,000 free a month per number. Utility templates sent inside the 24-hour window are charged too. Rates changed in some markets, and 9 markets got their own prices.' },
  { date: '2026-07-01', title: 'More countries get their own prices', text: 'Hong Kong, Hungary, Poland, Qatar, Romania and Singapore left their regional prices. Marketing went up in Italy, Spain and the UK.' },
  { date: '2026-04-01', title: 'New billing currencies', text: 'Eight more currencies, and a higher authentication-international rate for India.' },
  { date: '2026-01-01', title: 'Billing in rupees for India', text: 'Businesses billed in India can pay Meta in INR, and must move every WhatsApp account to INR by 31 December 2026. The India marketing rate went up.' },
  { date: '2025-07-01', title: 'Pay per message instead of per conversation', text: 'Meta started charging for each delivered template instead of each 24-hour conversation, made utility templates inside the window free, and added volume discounts.' },
  { date: '2024-11-01', title: 'Service conversations free', text: 'Normal replies were made free for everyone (this changed again on 1 October 2026).' },
];

export const TIER_STEPS = [
  { id: 'TIER_250', people: 250, label: '250', how: 'Every new business starts here.' },
  { id: 'TIER_2K', people: 2000, label: '2,000', how: 'Verify your business, or reach 2,000 people outside the window in 30 days with good-quality templates.' },
  { id: 'TIER_10K', people: 10000, label: '10,000', how: 'Automatic when quality is high and you used half your limit in the last 7 days.' },
  { id: 'TIER_100K', people: 100000, label: '1,00,000', how: 'Same rule: high quality and half your limit used in 7 days.' },
  { id: 'TIER_UNLIMITED', people: null, label: 'Unlimited', how: 'The top level, reached the same way.' },
];
export const tierIndex = (tier) => {
  const key = String(tier || '').toUpperCase();
  if (key === 'TIER_1K' || key === 'TIER_50') return key === 'TIER_1K' ? 1 : 0;
  return TIER_STEPS.findIndex((step) => step.id === key);
};
